import React from 'react';

/**
 * A pricing card component for displaying plan details, matching the design closely.
 *
 * Props:
 * - title: string               The plan name (e.g., "Basic")
 * - subtitle?: string           A short description under the title
 * - price: string | number      The plan cost (e.g., 40)
 * - billingCycleLabel?: string  The billing cycle label (e.g., "month billed annually")
 * - features: string[]          List of feature descriptions
 * - featuresHeading?: string    Heading text above the features list
 * - popular?: boolean           Whether to show the "Most popular" badge
 * - IconSvg?: React.ReactNode   Optional SVG/icon JSX above the title
 */
const PricingCard = ({
  title,
  subtitle,
  price,
  billingCycleLabel,
  features = [],
  featuresHeading = `What's included in ${title} plus:`,
  popular = false,
  IconSvg,
}) => (
  <div className="border border-gray-200 rounded-2xl p-6 shadow-md bg-white max-w-sm mx-auto transition-transform hover:scale-105">
    {popular && (
      <div className="flex items-center justify-center mb-4 px-3 py-1 text-xs font-semibold bg-yellow-200 text-gray-900 rounded-full">
        {/* Inline star icon */}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.955c.3.92-.755 1.688-1.539 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 00-.364-1.118L2.063 9.382c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.955z" />
        </svg>
        Most popular
      </div>
    )}

    <div className="flex flex-col items-center mb-4">
      {IconSvg && <div className="h-12 w-12 mb-2 text-gray-700">{IconSvg}</div>}
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      {subtitle && (
        <p className="text-center text-gray-500 text-sm mt-1">{subtitle}</p>
      )}
    </div>

    <p className="text-5xl font-extrabold text-gray-900 mb-4">
      €{price}
      {billingCycleLabel && (
        <span className="text-sm font-medium text-gray-500">/{billingCycleLabel}</span>
      )}
    </p>

    <button className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 rounded-lg transition">
      Get started
    </button>

    {features.length > 0 && (
      <>
        <p className="mt-6 text-sm font-medium text-gray-900">{featuresHeading}</p>
        <ul className="mt-2 space-y-3">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start">
              {/* Inline check icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 flex-shrink-0 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>
      </>
    )}
  </div>
);

export default PricingCard;
